import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    CloudSun, Thermometer, Wind, Eye, Layers,
    Loader2, RefreshCw, AlertTriangle, Check, Pencil, RotateCcw, Download
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import {
    getDayConditions,
    fetchMetarTaf,
    DayConditionsResponse,
    WeatherConditions,
} from '../../services/weatherService';

// ============================================
// TYPES
// ============================================

export interface WeatherParams {
    wcf: number;
    visibility_min_m: number;
    wind_speed_max_kt: number;
    wind_gust_max_kt: number | null;
    wind_direction_dominant: number | null;
    ceiling_min_ft: number | null;
    category: string;
    phenomena: string[];
    source: 'metar_auto' | 'manual_override';
}

interface WeatherConditionsPanelProps {
    icaoCode: string;
    defaultDate?: string;  // IATA busy day from DACAMS analysis (YYYY-MM-DD)
    onParamsChange: (params: Record<string, any>) => void;
}

// ============================================
// HELPERS
// ============================================

const PHENOMENA_LABELS: Record<string, { emoji: string; label_es: string; label_en: string }> = {
    RA: { emoji: '🌧️', label_es: 'Lluvia', label_en: 'Rain' },
    SN: { emoji: '❄️', label_es: 'Nieve', label_en: 'Snow' },
    FG: { emoji: '🌫️', label_es: 'Niebla', label_en: 'Fog' },
    BR: { emoji: '🌁', label_es: 'Bruma', label_en: 'Mist' },
    TS: { emoji: '⛈️', label_es: 'Tormenta', label_en: 'Thunderstorm' },
    DZ: { emoji: '🌦️', label_es: 'Llovizna', label_en: 'Drizzle' },
    GR: { emoji: '🧊', label_es: 'Granizo', label_en: 'Hail' },
    HZ: { emoji: '🌫️', label_es: 'Calima', label_en: 'Haze' },
    SH: { emoji: '🌧️', label_es: 'Chubascos', label_en: 'Showers' },
    FZ: { emoji: '🥶', label_es: 'Engelante', label_en: 'Freezing' },
};

function getPhenomenonDisplay(code: string, language: string) {
    // Try exact match, then check if any key is contained in code
    for (const [key, val] of Object.entries(PHENOMENA_LABELS)) {
        if (code.includes(key)) {
            return { emoji: val.emoji, label: language === 'es' ? val.label_es : val.label_en };
        }
    }
    return { emoji: '🌤️', label: code };
}

function getCategoryColor(category: string): string {
    switch (category) {
        case 'VMC': return 'emerald';
        case 'IMC': return 'amber';
        case 'LVP': return 'red';
        default: return 'gray';
    }
}

function getCategoryLabel(category: string, language: string): string {
    const labels: Record<string, { es: string; en: string }> = {
        VMC: { es: 'VMC — Buena visibilidad', en: 'VMC — Good visibility' },
        IMC: { es: 'IMC — Condiciones instrumentales', en: 'IMC — Instrument conditions' },
        LVP: { es: 'LVP — Baja visibilidad', en: 'LVP — Low Visibility Procedures' },
    };
    return labels[category]?.[language === 'es' ? 'es' : 'en'] ?? category;
}

function getWindDirection(deg: number | null): string {
    if (deg === null) return 'VRB';
    const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return dirs[Math.round(deg / 22.5) % 16];
}

function getWcfColor(wcf: number): string {
    if (wcf >= 0.95) return 'text-emerald-500';
    if (wcf >= 0.85) return 'text-green-500';
    if (wcf >= 0.75) return 'text-amber-500';
    if (wcf >= 0.60) return 'text-orange-500';
    return 'text-red-500';
}

// ============================================
// COMPONENT
// ============================================

const WeatherConditionsPanel: React.FC<WeatherConditionsPanelProps> = ({
    icaoCode,
    defaultDate,
    onParamsChange,
}) => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';

    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)]';
    const inputBg = isDark ? 'bg-[#F0EFEC] dark:bg-[var(--surface-inset)] border-[var(--border)] text-white' : 'bg-gray-50 border-gray-300 text-gray-900';

    const [selectedDate, setSelectedDate] = useState(defaultDate || '');
    const [loading, setLoading] = useState(false);
    const [downloadingMetar, setDownloadingMetar] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [weatherData, setWeatherData] = useState<DayConditionsResponse | null>(null);

    // Override state
    const [overrides, setOverrides] = useState<Record<string, number | null>>({});

    // Track last auto-fetched to avoid double-fetching
    const lastAutoFetch = useRef<string>('');

    // When defaultDate or icaoCode changes, update the date picker and auto-fetch
    useEffect(() => {
        if (!defaultDate || !icaoCode) return;
        const key = `${icaoCode}|${defaultDate}`;
        if (lastAutoFetch.current === key) return;
        lastAutoFetch.current = key;
        setSelectedDate(defaultDate);
        setOverrides({});
        setWeatherData(null);
        setError(null);

        // Auto-fetch conditions for the busy day
        (async () => {
            setLoading(true);
            try {
                const data = await getDayConditions(icaoCode, defaultDate);
                setWeatherData(data);
                if (data.conditions) {
                    onParamsChange({ ...data.simulation_parameters, date: defaultDate });
                }
            } catch (err: any) {
                setError(err.message || 'Error fetching weather data');
            } finally {
                setLoading(false);
            }
        })();
    }, [defaultDate, icaoCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const autoWcf = weatherData?.wcf ?? 1.0;
    const autoConditions = weatherData?.conditions;

    const getEffectiveValue = (key: string, autoValue: number | null): number | null => {
        return key in overrides ? overrides[key] : autoValue;
    };

    const isOverridden = (key: string) => key in overrides;

    const setOverride = (key: string, value: number | null) => {
        const newOverrides = { ...overrides, [key]: value };
        setOverrides(newOverrides);
        emitParams(autoConditions, newOverrides);
    };

    const resetOverride = (key: string) => {
        const newOverrides = { ...overrides };
        delete newOverrides[key];
        setOverrides(newOverrides);
        emitParams(autoConditions, newOverrides);
    };

    const resetAllOverrides = () => {
        setOverrides({});
        emitParams(autoConditions, {});
    };

    const emitParams = useCallback((conditions: WeatherConditions | undefined, currentOverrides: Record<string, number | null>) => {
        if (!conditions) return;

        const hasOverrides = Object.keys(currentOverrides).length > 0;
        const wcf = currentOverrides['wcf'] as number ?? autoWcf;

        const params: Record<string, any> = {
            weather: {
                wcf: wcf,
                visibility_min_m: currentOverrides['visibility'] as number ?? conditions.visibility_min,
                visibility_mean_m: conditions.visibility_mean,
                ceiling_min_ft: currentOverrides['ceiling'] as number ?? conditions.ceiling_min,
                wind_speed_mean_kt: conditions.wind_speed_mean,
                wind_speed_max_kt: currentOverrides['wind'] as number ?? conditions.wind_speed_max,
                wind_gust_max_kt: conditions.wind_gust_max,
                wind_direction_dominant: conditions.wind_direction_dominant,
                temperature_min_c: conditions.temperature_min,
                temperature_max_c: conditions.temperature_max,
                phenomena: conditions.phenomena,
                category: conditions.category,
                metar_count: conditions.metar_count,
            },
            wcf: wcf,
            source: hasOverrides ? 'manual_override' : 'metar_auto',
            date: selectedDate,
        };
        onParamsChange(params);
    }, [autoWcf, selectedDate, onParamsChange]);

    // Download METAR for the selected month (±15 days around the target) then re-fetch conditions
    const downloadAndFetch = async () => {
        if (!selectedDate || !icaoCode) return;
        setDownloadingMetar(true);
        setError(null);
        try {
            const d = new Date(selectedDate);
            // Download full month containing the target date
            const startDate = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
            const endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
            await fetchMetarTaf(icaoCode, startDate, endDate);
        } catch (err: any) {
            setError(err.message || 'Error downloading METAR data');
            setDownloadingMetar(false);
            return;
        }
        setDownloadingMetar(false);
        // Now fetch conditions for the specific day
        await fetchConditions();
    };

    const fetchConditions = async () => {
        if (!selectedDate || !icaoCode) return;

        setLoading(true);
        setError(null);
        setOverrides({});

        try {
            const data = await getDayConditions(icaoCode, selectedDate);
            setWeatherData(data);

            // Emit initial auto params
            if (data.conditions) {
                const params: Record<string, any> = {
                    ...data.simulation_parameters,
                    date: selectedDate,
                };
                onParamsChange(params);
            }
        } catch (err: any) {
            setError(err.message || 'Error fetching weather data');
        } finally {
            setLoading(false);
        }
    };

    const hasAnyOverride = Object.keys(overrides).length > 0;

    return (
        <div className={`p-6 rounded-xl border ${cardBg}`}>
            {/* HEADER */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-lg ${isDark ? 'bg-sky-500/20' : 'bg-sky-50'}`}>
                        <CloudSun className="text-sky-500" size={22} />
                    </div>
                    <div>
                        <h3 className={`text-lg font-bold ${textMain}`}>
                            {language === 'es' ? '3. Condiciones Meteorológicas' : '3. Weather Conditions'}
                        </h3>
                        <p className={`text-xs ${textSub}`}>
                            {language === 'es'
                                ? 'Ajuste automático de parámetros según METAR/TAF'
                                : 'Auto-adjust parameters from METAR/TAF data'}
                        </p>
                    </div>
                </div>
                {icaoCode && (
                    <span className={`px-3 py-1 rounded-full text-xs font-mono font-bold ${isDark ? 'bg-sky-900/50 text-sky-300' : 'bg-sky-100 text-sky-700'}`}>
                        {icaoCode}
                    </span>
                )}
            </div>

            {/* DATE PICKER ROW */}
            <div className="flex items-end gap-3 mb-5">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                        <label className={`block text-sm font-medium ${textSub}`}>
                            {language === 'es' ? 'Fecha de Simulación' : 'Simulation Date'}
                        </label>
                        {defaultDate && selectedDate === defaultDate && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide
                                ${isDark ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-700/40' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                                IATA Busy Day
                            </span>
                        )}
                    </div>
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={`w-full px-4 py-2.5 rounded-lg border ${inputBg} focus:ring-2 focus:ring-sky-500 focus:border-sky-500`}
                    />
                </div>
                <button
                    onClick={fetchConditions}
                    disabled={!selectedDate || !icaoCode || loading}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all
                        ${loading
                            ? 'bg-sky-400/50 text-white cursor-wait'
                            : 'bg-sky-500 hover:bg-sky-600 text-white hover:shadow-lg hover:shadow-sky-500/20 active:scale-95'
                        }
                        disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                    {language === 'es' ? 'Consultar' : 'Fetch'}
                </button>
            </div>

            {/* ERROR */}
            {error && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm mb-4 ${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
                    <AlertTriangle size={16} />
                    {error}
                </div>
            )}

            {/* NO DATA MESSAGE — with download button */}
            {weatherData && !weatherData.data_available && (
                <div className={`p-4 rounded-lg border text-sm mb-4 ${isDark ? 'bg-amber-900/20 border-amber-700/30 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="font-medium">{language === 'es' ? 'Datos METAR no disponibles' : 'METAR data not available'}</p>
                            <p className="text-xs mt-0.5 opacity-80">{weatherData.message}</p>
                        </div>
                    </div>
                    <button
                        onClick={downloadAndFetch}
                        disabled={downloadingMetar}
                        className={`mt-3 flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all w-full justify-center
                            ${isDark
                                ? 'bg-amber-700/40 hover:bg-amber-700/60 text-amber-200 border border-amber-600/40'
                                : 'bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300'
                            }
                            disabled:opacity-50 disabled:cursor-wait`}
                    >
                        {downloadingMetar ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
                        {downloadingMetar
                            ? (language === 'es' ? 'Descargando METAR...' : 'Downloading METAR...')
                            : (language === 'es'
                                ? `Descargar METAR de ${selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleString(language === 'es' ? 'es-ES' : 'en-GB', { month: 'long', year: 'numeric' }) : ''}`
                                : `Download METAR for ${selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleString('en-GB', { month: 'long', year: 'numeric' }) : ''}`)}
                    </button>
                </div>
            )}

            {/* WEATHER DATA DISPLAY */}
            {weatherData && autoConditions && (
                <div className="space-y-4">
                    {/* Category + WCF header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold
                                bg-${getCategoryColor(autoConditions.category)}-500/20
                                text-${getCategoryColor(autoConditions.category)}-${isDark ? '400' : '600'}`}>
                                {autoConditions.category === 'VMC' && '✅'}
                                {autoConditions.category === 'IMC' && '⚠️'}
                                {autoConditions.category === 'LVP' && '🔴'}
                                {getCategoryLabel(autoConditions.category, language)}
                            </span>
                            {weatherData.data_available && (
                                <span className={`text-xs ${textSub}`}>
                                    ({autoConditions.metar_count} METARs)
                                </span>
                            )}
                        </div>
                        {hasAnyOverride && (
                            <button
                                onClick={resetAllOverrides}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                                    ${isDark ? 'text-sky-400 hover:bg-sky-500/10' : 'text-sky-600 hover:bg-sky-50'}`}
                            >
                                <RotateCcw size={12} />
                                {language === 'es' ? 'Resetear todo' : 'Reset all'}
                            </button>
                        )}
                    </div>

                    {/* GRID: 4 Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {/* WCF Card */}
                        <MetricCard
                            icon={<Layers size={18} />}
                            label="WCF"
                            value={
                                <input
                                    type="number"
                                    step="0.05"
                                    min="0"
                                    max="1"
                                    value={getEffectiveValue('wcf', autoWcf) ?? autoWcf}
                                    onChange={(e) => setOverride('wcf', parseFloat(e.target.value) || 0)}
                                    className={`w-full bg-transparent border-0 text-2xl font-bold p-0 focus:outline-none focus:ring-0 ${getWcfColor(getEffectiveValue('wcf', autoWcf) ?? autoWcf)}`}
                                />
                            }
                            isOverridden={isOverridden('wcf')}
                            onReset={() => resetOverride('wcf')}
                            isDark={isDark}
                            textSub={textSub}
                            language={language}
                        />

                        {/* Visibility Card */}
                        <MetricCard
                            icon={<Eye size={18} />}
                            label={language === 'es' ? 'Visibilidad' : 'Visibility'}
                            value={
                                <div className="flex items-baseline gap-1">
                                    <input
                                        type="number"
                                        step="100"
                                        min="0"
                                        max="9999"
                                        value={getEffectiveValue('visibility', autoConditions.visibility_min) ?? 9999}
                                        onChange={(e) => setOverride('visibility', parseInt(e.target.value) || 0)}
                                        className={`w-full bg-transparent border-0 text-xl font-bold p-0 focus:outline-none focus:ring-0 ${textMain}`}
                                    />
                                    <span className={`text-xs ${textSub}`}>m</span>
                                </div>
                            }
                            isOverridden={isOverridden('visibility')}
                            onReset={() => resetOverride('visibility')}
                            isDark={isDark}
                            textSub={textSub}
                            language={language}
                        />

                        {/* Wind Card */}
                        <MetricCard
                            icon={<Wind size={18} />}
                            label={language === 'es' ? 'Viento' : 'Wind'}
                            value={
                                <div className="flex items-baseline gap-1">
                                    <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={getEffectiveValue('wind', autoConditions.wind_speed_max) ?? 0}
                                        onChange={(e) => setOverride('wind', parseInt(e.target.value) || 0)}
                                        className={`w-full bg-transparent border-0 text-xl font-bold p-0 focus:outline-none focus:ring-0 ${textMain}`}
                                    />
                                    <span className={`text-xs ${textSub}`}>kt</span>
                                </div>
                            }
                            subtitle={autoConditions.wind_direction_dominant != null
                                ? `${getWindDirection(autoConditions.wind_direction_dominant)} (${autoConditions.wind_direction_dominant}°)${autoConditions.wind_gust_max ? ` G${autoConditions.wind_gust_max}kt` : ''}`
                                : 'VRB'}
                            isOverridden={isOverridden('wind')}
                            onReset={() => resetOverride('wind')}
                            isDark={isDark}
                            textSub={textSub}
                            language={language}
                        />

                        {/* Ceiling / Temperature Card */}
                        <MetricCard
                            icon={<Thermometer size={18} />}
                            label={language === 'es' ? 'Techo / Temp' : 'Ceiling / Temp'}
                            value={
                                <div>
                                    <div className={`text-xl font-bold ${textMain}`}>
                                        {autoConditions.ceiling_min != null ? `${autoConditions.ceiling_min} ft` : '—'}
                                    </div>
                                    <div className={`text-xs ${textSub}`}>
                                        {autoConditions.temperature_min}°C / {autoConditions.temperature_max}°C
                                    </div>
                                </div>
                            }
                            isDark={isDark}
                            textSub={textSub}
                            language={language}
                        />
                    </div>

                    {/* PHENOMENA BADGES */}
                    {autoConditions.phenomena.length > 0 && (
                        <div>
                            <p className={`text-xs font-medium mb-2 ${textSub}`}>
                                {language === 'es' ? 'Fenómenos Meteorológicos' : 'Weather Phenomena'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {autoConditions.phenomena.map((p, i) => {
                                    const { emoji, label } = getPhenomenonDisplay(p, language);
                                    return (
                                        <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                                            ${isDark ? 'bg-gray-700/60 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                                            {emoji} {label}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* STATUS BAR */}
                    <div className={`flex items-center justify-between p-3 rounded-lg text-xs
                        ${'bg-[var(--surface-raised)]'}`}>
                        <div className={`flex items-center gap-2 ${textSub}`}>
                            {hasAnyOverride ? (
                                <>
                                    <Pencil size={12} className="text-amber-500" />
                                    <span className="text-amber-500 font-medium">
                                        {language === 'es' ? 'Override manual activo' : 'Manual override active'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Check size={12} className="text-emerald-500" />
                                    <span className="text-emerald-500 font-medium">
                                        {language === 'es' ? 'Valores automáticos desde METAR' : 'Auto values from METAR'}
                                    </span>
                                </>
                            )}
                        </div>
                        <span className={textSub}>
                            {weatherData.date}
                        </span>
                    </div>
                </div>
            )}

            {/* EMPTY STATE */}
            {!weatherData && !loading && (
                <div className={`py-8 text-center rounded-lg border-2 border-dashed ${'border-[var(--border)]'}`}>
                    <CloudSun className={`mx-auto mb-3 ${textSub}`} size={32} />
                    <p className={`text-sm ${textSub}`}>
                        {defaultDate
                            ? (language === 'es'
                                ? `Busy Day IATA: ${defaultDate} — pulsa Consultar para cargar condiciones`
                                : `IATA Busy Day: ${defaultDate} — click Fetch to load conditions`)
                            : (language === 'es'
                                ? 'Selecciona una fecha para consultar las condiciones meteorológicas'
                                : 'Select a date to fetch weather conditions')}
                    </p>
                    {!icaoCode && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                            {language === 'es'
                                ? '⚠️ Primero selecciona un proyecto con código ICAO'
                                : '⚠️ First select a project with ICAO code'}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};


// ============================================
// METRIC CARD SUB-COMPONENT
// ============================================

interface MetricCardProps {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    subtitle?: string;
    isOverridden?: boolean;
    onReset?: () => void;
    isDark: boolean;
    textSub: string;
    language: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
    icon, label, value, subtitle, isOverridden, onReset, isDark, textSub, language
}) => (
    <div className={`relative p-3.5 rounded-lg border transition-shadow
        ${isDark ? 'bg-[#F0EFEC] dark:bg-[var(--surface-inset)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}
        ${isOverridden ? (isDark ? 'ring-1 ring-amber-500/40' : 'ring-1 ring-amber-400/50') : ''}
        hover:shadow-md`}
    >
        <div className="flex items-center justify-between mb-2">
            <div className={`flex items-center gap-1.5 ${textSub}`}>
                {icon}
                <span className="text-xs font-medium">{label}</span>
            </div>
            {isOverridden && onReset && (
                <button
                    onClick={onReset}
                    title={language === 'es' ? 'Resetear' : 'Reset'}
                    className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-600 text-amber-400' : 'hover:bg-gray-200 text-amber-600'}`}
                >
                    <RotateCcw size={12} />
                </button>
            )}
        </div>
        <div>{value}</div>
        {subtitle && <p className={`text-[10px] mt-1 ${textSub}`}>{subtitle}</p>}
        {isOverridden !== undefined && (
            <div className={`absolute top-2 right-2 w-1.5 h-1.5 rounded-full
                ${isOverridden ? 'bg-amber-500' : 'bg-emerald-500'}`}
            />
        )}
    </div>
);


export default WeatherConditionsPanel;
